'use client';

import { useAuth } from '@clerk/nextjs';
import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { getLastOrgId, setLastOrgId } from '@hooks/use-pwa';

const SHORTCUT_ROUTES: Record<string, string> = {
  '86list': '86list',
  messages: 'messages',
  recipes: 'bev/recipes'
};

/**
 * Remembers the active org for installed-app launches and manifest shortcuts.
 */
export function RememberLastOrg({
  organization_id
}: {
  organization_id: string;
}) {
  const { userId } = useAuth();

  useEffect(() => {
    if (!userId) return;
    setLastOrgId(userId, organization_id);
  }, [organization_id, userId]);

  return null;
}

/**
 * On `/`, redirect standalone launches to the last org (or a shortcut target).
 */
export function PwaLaunchRedirect() {
  const router = useRouter();
  const { userId, isLoaded } = useAuth();

  useEffect(() => {
    if (!isLoaded) return;

    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      ('standalone' in navigator &&
        (navigator as Navigator & { standalone?: boolean }).standalone ===
          true);
    const params = new URLSearchParams(window.location.search);
    const shortcut = params.get('shortcut');
    const fromPwa =
      standalone || params.get('source') === 'pwa' || Boolean(shortcut);
    if (!fromPwa) return;

    if (!userId) return;

    let subpath = 'dashboard';
    if (shortcut && SHORTCUT_ROUTES[shortcut]) {
      subpath = SHORTCUT_ROUTES[shortcut];
    }

    const org = getLastOrgId(userId);
    if (org) {
      router.replace(`/${org}/${subpath}`);
    }
  }, [router, userId, isLoaded]);

  return null;
}
